import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';
import { ACL } from 'components/common';

const RequestCard = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 24px;
	margin-bottom: 16px;
	transition: box-shadow 0.3s;

	&:hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}
`;

const CardBody = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	gap: 24px;
`;

const InfoColumns = styled(Box)`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 20px 40px;
	flex: 1;
`;

const InfoField = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	font-weight: 400;
	line-height: 1.4;
`;

const FieldValue = styled(Text)`
	font-size: 15px;
	color: #000;
	font-weight: 600;
	line-height: 1.5;
`;

const ActionSection = styled(Flex)`
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	flex-shrink: 0;
`;

const ViewSection = styled(Flex)`
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	padding: 0 12px;
`;

const ViewButton = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	background: #e6f7ff;
	border-radius: 4px;
	border: none;
	cursor: pointer;
	color: #000;
	font-size: 16px;
	transition: background 0.2s ease;

	&:hover {
		background: #bae7ff;
	}

	&:active {
		background: #91d5ff;
	}
`;

const ApproveButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;

	padding: 8px 18px;
	min-width: 110px;

	background: #fff;
	color: #52c41a;

	border: 1.5px solid #52c41a;
	border-radius: 6px;

	font-size: 14px;
	font-weight: 500;
	cursor: pointer;

	transition: all 0.25s ease;

	.anticon {
		font-size: 16px;
		color: #52c41a;
	}

	&:hover {
		background: #f6ffed;
		border-color: #73d13d;
		color: #73d13d;
	}

	&:active {
		background: #d9f7be;
		border-color: #52c41a;
	}

	&:disabled {
		background: #f5f5f5;
		color: #bfbfbf;
		border-color: #d9d9d9;
		cursor: not-allowed;
		pointer-events: none;

		.anticon {
			color: #bfbfbf;
		}
	}
`;

const RejectButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	padding: 10px 20px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s;
	min-width: 110px;

	.anticon {
		font-size: 16px;
	}

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
		transform: translateY(-1px);
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
		transform: translateY(0);
	}

	&:disabled {
		background: #f5f5f5;
		color: #bfbfbf;
		border-color: #d9d9d9;
		cursor: not-allowed;
		pointer-events: none;
		transform: none;
	}
`;

const DocumentLink = styled.span`
	color: #1890ff;
	text-decoration: none;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;

	&:hover {
		text-decoration: underline;
		color: #40a9ff;
	}
`;

export default function VehicleReplacementCardV2({ replacement, onView, onApprove, onReject }) {
	const [filePreviewVisible, setFilePreviewVisible] = useState(false);
	const [previewFileKey, setPreviewFileKey] = useState(null);
	const [previewFileName, setPreviewFileName] = useState('');

	const handleViewDocument = (fileKey, fileName = '') => {
		if (!fileKey) return;
		setPreviewFileKey(fileKey);
		setPreviewFileName(fileName);
		setFilePreviewVisible(true);
	};

	const handleFilePreviewClose = () => {
		setFilePreviewVisible(false);
		setPreviewFileKey(null);
		setPreviewFileName('');
	};

	return (
		<>
			<RequestCard>
				<CardBody>
					<InfoColumns>
						<InfoField>
							<FieldLabel>Vehicle Number :</FieldLabel>
							<FieldValue>
								<strong>{replacement.vehicle_number || '-'}</strong>
							</FieldValue>
						</InfoField>

						<InfoField>
							<FieldLabel>Vendor Name :</FieldLabel>
							<FieldValue>
								<strong>{replacement.vendor_name || '-'}</strong>
							</FieldValue>
						</InfoField>

						<InfoField>
							<FieldLabel>Vehicle Type :</FieldLabel>
							<FieldValue>
								<strong>{replacement.vehicle_type || '-'}</strong>
							</FieldValue>
						</InfoField>

						<InfoField>
							<FieldLabel>RC Document :</FieldLabel>
							<FieldValue>
								{replacement.rc_document_url || replacement.rc_document_name ? (
									<DocumentLink
										onClick={() =>
											handleViewDocument(
												replacement.rc_document_url,
												'RC Document'
											)
										}
									>
										{replacement.rc_document_name || 'View RC Document'}
									</DocumentLink>
								) : (
									'-'
								)}
							</FieldValue>
						</InfoField>

						<InfoField>
							<FieldLabel>Insurance Document :</FieldLabel>
							<FieldValue>
								{replacement.insurance_document_url ||
								replacement.insurance_document_name ? (
									<DocumentLink
										onClick={() =>
											handleViewDocument(
												replacement.insurance_document_url,
												'Insurance Document'
											)
										}
									>
										{replacement.insurance_document_name ||
											'View Insurance Document'}
									</DocumentLink>
								) : (
									'-'
								)}
							</FieldValue>
						</InfoField>

						<InfoField>
							<FieldLabel>City :</FieldLabel>
							<FieldValue>{replacement.city || '-'}</FieldValue>
						</InfoField>

						<InfoField>
							<FieldLabel>Created By :</FieldLabel>
							<FieldValue>{replacement.created_by || '-'}</FieldValue>
						</InfoField>
					</InfoColumns>
					<ViewSection>
						<ViewButton onClick={() => onView(replacement.id)}>
							<EyeOutlined />
						</ViewButton>
					</ViewSection>
					<ActionSection>
						<ACL action="update" module="handle_vehicle_replacement_approval">
							<ApproveButton onClick={() => onApprove(replacement.id)}>
								<CheckOutlined />
								Approve
							</ApproveButton>
						</ACL>
						<ACL action="update" module="handle_vehicle_replacement_approval">
							<RejectButton onClick={() => onReject(replacement.id)}>
								<CloseOutlined />
								Reject
							</RejectButton>
						</ACL>
					</ActionSection>
				</CardBody>
			</RequestCard>
			<FilePreviewModal
				visible={filePreviewVisible}
				fileKey={previewFileKey}
				fileName={previewFileName}
				entity="vehicle"
				onClose={handleFilePreviewClose}
			/>
		</>
	);
}

VehicleReplacementCardV2.propTypes = {
	replacement: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		vehicle_number: PropTypes.string,
		vendor_name: PropTypes.string,
		vehicle_type: PropTypes.string,
		rc_document_url: PropTypes.string,
		rc_document_name: PropTypes.string,
		insurance_document_url: PropTypes.string,
		insurance_document_name: PropTypes.string,
		city: PropTypes.string,
		created_by: PropTypes.string,
	}).isRequired,
	onView: PropTypes.func.isRequired,
	onApprove: PropTypes.func.isRequired,
	onReject: PropTypes.func.isRequired,
};
